import React, { useEffect, useRef, useState, DragEvent } from 'react';

import { equals } from 'ramda';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import CreatableSelect from 'react-select/creatable';
import { components, SingleValueProps } from 'react-select';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { fenceHistory, history } from '../../../stores/store';
import { iFullStoreState, iNote, UserAuth } from '../../../shared/interfaces';
import * as notesdb from '../../../shared/db/notes-db';

import { keyGen } from '../../../shared/firebase';
import { uploadNoteFile, uploadDynamicFieldFile as uploadDynamicFieldFileUtil } from '../../../shared/files';
import { withSentry } from '../../../shared/hoc/withSentry';
import Dialog, { DialogConfigSetter } from '../../Dialog';
import ModalActions from '../../menus/modal/modal-actions';
import ModalHeader from '../../menus/modal/modal-header';
import ActionRow from '../../fence/tab-bodies/ActionRow/ActionRow';
import DashboardBlock from '../../DashboardBlock';
import FenceBodyLayout from '../../fence/tab-bodies/BodyLayout/FenceBodyLayout/FenceBodyLayout';
import { useFileUpload } from '../../../hooks/useFileUpload';
import { Checkbox } from '../../Checkbox';
import DayPicker from 'react-day-picker';
import MaskedInput from 'react-text-mask';
import { CALENDAR_INPUT_FORMAT, CustomNavBar, dateMask } from '../../CalendarInput/CalendarInput';
import Loader from '../Billing/Other/Loader';
import Modal from '../../Modal';
import { ReactComponent as CloseIcon } from '../../../assets/svg/close-icon.svg';
import 'react-day-picker/lib/style.css';
import '../tags-info/DayPicker.scss';
import NewNoteFieldModal from './NewNoteFieldModal';
import {
  NoteFieldKind,
  NoteFieldMap,
  getAllNoteFields,
  updateNoteField,
  deleteNoteField
} from '../../../shared/db/note-extra-fields-db';
import styles from './add-note.module.scss';
// import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import { Fa } from '../../elements/fa';
import { faEyeSlash, faPencilAlt, faFile } from '@fortawesome/fontawesome-free-solid';
import GalleryModal, { GalleryFile } from '../../elements/GalleryModal/GalleryModal';
import EditOptionModal from './EditOptionModal';
import ClearModal from '../../ClearModal/ClearModal';

type IRouteParams = {
  deviceOrFenceId: string;
  deviceOrFence: 'device' | 'fence';
  action: 'add' | 'edit';
  noteId: string;
};

type ExtraFieldValue = string | boolean | number | null | iNote['file'];

const FIELD_LABELS: Record<string, string> = {
  date: 'Creation Date',
  note: 'Note',
  po: 'Purchase Order',
  techName: 'Tech Name',
  completedDate: 'Completed Date',
  amount: 'Amount',
  complete: 'Complete',
  file: 'Files',
};

const VALID_NOTE_FIELD_KINDS: NoteFieldKind[] = [
  'text',
  'number',
  'boolean',
  'textarea',
  'date',
  'select',
  'file',
];

const normalizeNoteFieldKind = (kind: unknown): NoteFieldKind => {
  if (typeof kind !== 'string') return 'text';
  if (VALID_NOTE_FIELD_KINDS.includes(kind as NoteFieldKind)) {
    return kind as NoteFieldKind;
  }
  return 'text';
};

const formatFieldValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

type SortableDragProps = {
  attributes: React.HTMLAttributes<HTMLDivElement>;
  listeners: Record<string, any> | undefined;
};

interface CustomSelectProps {
  fieldId: string;
  onEditClick: (fieldId: string, value: string) => void;
  onHideClick: (fieldId: string, value: string) => void;
}

const CustomSelectOption = (props: any) => {
  const { fieldId, onEditClick, onHideClick } = props.selectProps as CustomSelectProps;
  const { data } = props;

  return (
    <components.Option {...props}>
      <div className={styles.optionRow}>
        <span className={styles.optionText}>{props.children}</span>

        <div className={styles.optionActions}>
          <div
            className={styles.iconBtn}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onEditClick(fieldId, data.value);
            }}
            title="Edit text"
          >
            <Fa icon={faPencilAlt} />
          </div>

          <div
            className={styles.iconBtn}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onHideClick(fieldId, data.value);
            }}
            title="Hide suggestion"
          >
            <Fa icon={faEyeSlash} />
          </div>
        </div>
      </div>
    </components.Option>
  );
};

const SortableRow = ({
  id,
  children,
}: {
  id: string;
  children: (dragProps: SortableDragProps) => React.ReactNode;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const noScaleTransform = transform
    ? { ...transform, scaleX: 1, scaleY: 1 }
    : null;

  const style: React.CSSProperties = {
    transform: transform ? CSS.Transform.toString(noScaleTransform) : undefined,
    transition,
    opacity: isDragging ? 0.7 : 1,
    willChange: isDragging ? 'transform' : undefined,
    transformOrigin: '0 0',
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ attributes, listeners })}
    </div>
  );
};

const CopyableSingleValue = (props: SingleValueProps<any, false>) => (
  <components.SingleValue {...props}>
    <span
      style={{ userSelect: 'text', cursor: 'text', pointerEvents: 'auto' }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {props.children}
    </span>
  </components.SingleValue>
);

const AddNote = () => {
  const { deviceOrFenceId, deviceOrFence, noteId, action } =
    useParams<IRouteParams>();

  const location = useLocation<{
    mainRouterProps?: { itemType: string; itemId: string };
    ownerTitle?: string;
  }>();
  const ownerTitle = location.state?.ownerTitle;

  const authUser = useSelector<iFullStoreState, UserAuth | undefined>(
    (state) => state.auth?.user ?? undefined,
    (l, r) => equals(l, r),
  );

  const device = useSelector<iFullStoreState, string | undefined>(
    (state) => {
      if (deviceOrFence === 'device') {
        return state.devicesData.devicesDetails.getIn([deviceOrFenceId, 'name']) as string;
      }
      return undefined;
    },
    (l, r) => equals(l, r),
  );

  const vehicleName = device;

  const REMOVED_LEGACY_FIELDS = ['part', 'service', 'vendor', 'repairShop'];
  const DEFAULT_ORDER = ['date', 'po', 'techName', 'completedDate', 'amount', 'complete', 'file', 'note'];
  const DEFAULT_VISIBLE_FIELDS = ['date', 'po', 'note'];

  const [fieldOrder, setFieldOrder] = useState<string[] | null>(null);
  const [globalVisibleFields, setGlobalVisibleFields] = useState<string[]>(DEFAULT_VISIBLE_FIELDS);
  const [isLoading, setIsLoading] = useState(true);

  const [note, updateNote] = useState('');
  const [po, setPo] = useState('D');
  const [existingFiles, setExistingFiles] = useState<iNote['file'][]>([]);
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [techName, setTechName] = useState('');
  const [completedDate, setCompletedDate] = useState('');
  const [amount, setAmount] = useState('');
  const [complete, setComplete] = useState(false);
  const [creationDate, setCreationDate] = useState(moment().format('MM-DD-YYYY'));
  const [optionToEdit, setOptionToEdit] = useState<{ fieldId: string; value: string } | null>(null);
  const [optionToDelete, setOptionToDelete] = useState<{ fieldId: string; value: string } | null>(null);

  const [hiddenOptions, setHiddenOptions] = useState<Record<string, Record<string, boolean>>>({});
  const [renameOptions, setRenameOptions] = useState<Record<string, Record<string, string>>>({});

  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [noteFieldDefs, setNoteFieldDefs] = useState<NoteFieldMap>({});
  const [extraValues, setExtraValues] = useState<iNote['extraValues'] | undefined>(undefined);
  const [isCreateFieldOpen, setIsCreateFieldOpen] = useState(false);
  const [dynamicFieldFiles, setDynamicFieldFiles] = useState<Record<string, File>>({});
  const [dynamicFieldUploading, setDynamicFieldUploading] = useState<Record<string, boolean>>({});
  const [dynamicSelectInputValue, setDynamicSelectInputValue] = useState<Record<string, string>>({});
  const [dynamicSelectOptions, setDynamicSelectOptions] = useState<Record<string, string[]>>({});

  const [techNameSuggestions, setTechNameSuggestions] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [mainDropZoneHovered, setMainDropZoneHovered] = useState(false);
  const [dynamicDropZonesHovered, setDynamicDropZonesHovered] = useState<Record<string, boolean>>({});
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryFiles, setGalleryFiles] = useState<GalleryFile[]>([]);
  const [galleryIndex, setGalleryIndex] = useState(0);

  // Date picker modal state
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [currentDateFieldId, setCurrentDateFieldId] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [displayMonth, setDisplayMonth] = useState<Date>(new Date());

  const {
    uploading,
    error,
  } = useFileUpload();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const getPrefKey = (value: string) => notesdb.encodeSuggestionKey(String(value || ''));

  const resolveSuggestionLabel = (fieldId: string, originalValue: string): string => {
    const startValue = String(originalValue || '').trim();
    if (!startValue) return '';

    let resolvedValue = startValue;
    const seenValues = new Set<string>();

    for (let i = 0; i < 8; i += 1) {
      const currentKey = getPrefKey(resolvedValue);
      const renamedTo = renameOptions[fieldId]?.[currentKey];

      if (!renamedTo || !String(renamedTo).trim()) break;

      const nextValue = String(renamedTo).trim();

      if (!nextValue || nextValue === resolvedValue || seenValues.has(nextValue)) {
        resolvedValue = nextValue || resolvedValue;
        break;
      }

      seenValues.add(resolvedValue);
      resolvedValue = nextValue;
    }

    return resolvedValue;
  };

    const isSuggestionHidden = (fieldId: string, value: string): boolean => {
      const rawValue = String(value || '').trim();
      if (!rawValue) return false;

      const rawPrefKey = getPrefKey(rawValue);

      const resolvedLabel = resolveSuggestionLabel(fieldId, rawValue);
      const resolvedPrefKey = resolvedLabel ? getPrefKey(resolvedLabel) : '';

      return !!hiddenOptions[fieldId]?.[rawPrefKey] || (!!resolvedPrefKey && !!hiddenOptions[fieldId]?.[resolvedPrefKey]);
    };

    const filterOptionLeftToRight = (option: any, inputValue: string) => {
      if (!inputValue) return true;
      return option.label.toLowerCase().startsWith(inputValue.toLowerCase());
    };

    const openGallery = (files: GalleryFile[], startIndex: number) => {
      setGalleryFiles(files);
      setGalleryIndex(startIndex);
      setIsGalleryOpen(true);
    };

    const loadNoteInitData = async () => {
      return Promise.all([
        action === 'edit' ? notesdb.getNote(deviceOrFenceId, deviceOrFence, noteId) : Promise.resolve(null),
        notesdb.getFieldOrder(),
        notesdb.getAllTechNames().catch(() => []),
        getAllNoteFields().catch(() => ({})),
        notesdb.getGlobalVisibleFields?.().catch(() => null) ?? Promise.resolve(null),
        notesdb.getAllSelectOptions().catch((e) => { console.error('Error loading select options:', e); return {}; }),
        notesdb.getSuggestionPrefs().catch(() => ({ hidden: {}, rename: {} })),
      ]);
    };

    const loadStandardNoteFields = (noteData: iNote) => {
      if (noteData.note !== undefined && noteData.note !== null) updateNote(noteData.note);
      if (noteData.po !== undefined && noteData.po !== null) setPo(noteData.po);
      if (noteData.techName !== undefined && noteData.techName !== null) setTechName(noteData.techName);
      if (noteData.completedDate !== undefined && noteData.completedDate !== null) setCompletedDate(noteData.completedDate);
      if (typeof noteData.amount === 'number') setAmount(String(noteData.amount));
      if (typeof noteData.complete === 'boolean') setComplete(noteData.complete);
      if (noteData.date) setCreationDate(moment(noteData.date).format('MM-DD-YYYY'));
    };

    const loadNoteFiles = (noteData: iNote) => {
      const dbFiles = noteData.file ? (Array.isArray(noteData.file) ? noteData.file : [noteData.file]) : [];
      setExistingFiles(dbFiles.filter(f => f?.url));
    };

    const initializeExtraValues = (fieldDefs: NoteFieldMap, existing?: iNote['extraValues']) => {
      const merged: iNote['extraValues'] = { ...existing };

      Object.entries(fieldDefs).forEach(([fieldId, fieldDef]) => {
        if (merged[fieldId]) return;
        const kind = (fieldDef?.details?.kind as NoteFieldKind) || 'text';
        merged[fieldId] = {
          fieldId,
          value: kind === 'boolean' ? false : ''
        };
      });

      return merged;
    };

  const buildSelectOptions = (
    extraValues: iNote['extraValues'],
    fieldDefs: NoteFieldMap,
    globalOpts: Record<string, string[]>
  ): Record<string, string[]> => {
    const selectOpts: Record<string, string[]> = {};

    Object.entries(extraValues || {}).forEach(([fid, entry]) => {
      if (fieldDefs[fid]?.details?.kind !== 'select') return;

      const e = entry as { value?: unknown; options?: string[] };
      const opts = Array.isArray(e?.options) && e.options.length > 0 ? e.options : null;
      const v = e?.value;
      const noteOpts = opts ?? (v != null && typeof v === 'string' && v.trim() ? [v.trim()] : []);

      const merged = [...new Set([...(globalOpts[fid] ?? []), ...noteOpts])].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }),
      );

      selectOpts[fid] = merged;
    });

    return selectOpts;
  };

  useEffect(() => {
    const initData = async () => {
      setIsLoading(true);

      try {
        const [noteData, savedOrder, allTechNames, allNoteFields, savedVisibleFields, allSelectOptions, suggestionPrefs] = await loadNoteInitData();
        setTechNameSuggestions(Array.isArray(allTechNames) ? allTechNames : []);
        const fieldDefs = (allNoteFields || {}) as NoteFieldMap;
        setNoteFieldDefs(fieldDefs);

        setHiddenOptions(suggestionPrefs?.hidden || {});
        setRenameOptions(suggestionPrefs?.rename || {});

        if (savedVisibleFields && Array.isArray(savedVisibleFields)) {
          setGlobalVisibleFields(savedVisibleFields);
        }

        let allDynamicFieldIds: string[] = [];

        if (noteData) {
          loadStandardNoteFields(noteData);
          loadNoteFiles(noteData);

          const existingExtra = noteData.extraValues ?? {};

          const mergedExtra = initializeExtraValues(fieldDefs, existingExtra);

          allDynamicFieldIds = Object.keys(mergedExtra);

          setExtraValues(mergedExtra);

          const extra = mergedExtra;
          const globalOpts = (allSelectOptions || {}) as Record<string, string[]>;
          const selectOpts = buildSelectOptions(mergedExtra, fieldDefs, globalOpts);
          if (Object.keys(selectOpts).length > 0) setDynamicSelectOptions(selectOpts);
        }

        let finalOrder = DEFAULT_ORDER;

        if (savedOrder && Array.isArray(savedOrder) && savedOrder.length > 0) {
          const sanitizedSavedOrder = savedOrder.filter((item) => !REMOVED_LEGACY_FIELDS.includes(item));
          const mergedOrder = [
            ...sanitizedSavedOrder,
            ...DEFAULT_ORDER.filter((item) => !sanitizedSavedOrder.includes(item)),
          ];
          finalOrder = mergedOrder;
        }
        if (!noteData && action === 'add') {
          const dynamicIds = Object.keys(fieldDefs);

          if (dynamicIds.length > 0) {
            const initialExtraValues: iNote['extraValues'] = {};

            dynamicIds.forEach((fieldId) => {
              const def = fieldDefs[fieldId]?.details;
              const kind: NoteFieldKind = (def?.kind as NoteFieldKind | undefined) || 'text';

              let initialValue: string | boolean = '';
              if (kind === 'boolean') {
                initialValue = false;
              }

              initialExtraValues[fieldId] = { fieldId, value: initialValue };
            });

            setExtraValues(initialExtraValues);
            allDynamicFieldIds = dynamicIds;

            const globalOpts = (allSelectOptions || {}) as Record<string, string[]>;
            const globalSelectOpts: Record<string, string[]> = {};
            dynamicIds.forEach((fid) => {
              if (fieldDefs[fid]?.details?.kind === 'select') {
                globalSelectOpts[fid] = globalOpts[fid] ?? [];
              }
            });
            if (Object.keys(globalSelectOpts).length > 0) {
              setDynamicSelectOptions(globalSelectOpts);
            }
          }
        }

        if (allDynamicFieldIds.length > 0) {
          finalOrder = [
            ...finalOrder,
            ...allDynamicFieldIds.filter((id) => !finalOrder.includes(id)),
          ];
        }
        setFieldOrder(finalOrder);
      } catch (err) {
        console.error(err);
        setFieldOrder(DEFAULT_ORDER);
      } finally {
        setIsLoading(false);
      }
    };

    initData();
  }, []);

  const back = (isFence: boolean) =>
    isFence ? fenceHistory.goBack() : history.goBack();

  const uploadDynamicFieldFile = async (fieldId: string, file: File): Promise<iNote['file'] | null> => {
    if (deviceOrFence === 'fence') return null;

    setDynamicFieldUploading(prev => ({ ...prev, [fieldId]: true }));

    try {
      return await uploadDynamicFieldFileUtil(file, deviceOrFenceId, fieldId);
    } finally {
      setDynamicFieldUploading(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  const update = async () => {
    if (!authUser || !deviceOrFenceId || !noteId) return;

    const uploadPromises = filesToUpload.map(f => uploadNoteFile(f, deviceOrFenceId || ""));
    const newlyUploaded = await Promise.all(uploadPromises);
    const validUploads = newlyUploaded.filter((f): f is NonNullable<iNote['file']> => !!f);

    const finalFilesList = [...existingFiles, ...validUploads].map(f => ({
      fileName: f?.fileName || '',
      url: f?.url || '',
      format: f?.format || (f?.url.includes('.pdf') ? 'pdf' : 'image')
    }));

    const base = extraValuesRef.current ?? {};
    const updatedExtraValues: iNote['extraValues'] = { ...base };

    for (const [fieldId, fileToUpload] of Object.entries(dynamicFieldFiles)) {
      if (fileToUpload && fileToUpload instanceof File && fieldId) {
        const fd = await uploadDynamicFieldFile(fieldId, fileToUpload);
        if (fd) {
          updatedExtraValues[fieldId] = { fieldId, value: fd };
        }
      }
    }

    for (const [fieldId, typed] of Object.entries(dynamicSelectInputValue)) {
      const trimmed = typeof typed === 'string' ? typed.trim() : '';
      if (!trimmed) continue;
      if (noteFieldDefs[fieldId]?.details?.kind !== 'select') continue;
      updatedExtraValues[fieldId] = { fieldId, value: trimmed };
    }

    type ExtraWithOptions = iNote['extraValues'] & Record<string, { fieldId: string; value: unknown; options?: string[] }>;
    const optsMap = dynamicSelectOptionsRef.current ?? {};
    for (const fieldId of Object.keys(updatedExtraValues || {})) {
      if (noteFieldDefs[fieldId]?.details?.kind !== 'select') continue;
      const opts = optsMap[fieldId] ?? [];
      const entry = updatedExtraValues[fieldId];
      (updatedExtraValues as ExtraWithOptions)[fieldId] = { ...entry, fieldId, options: opts };
    }

    const amountNumber = amount.trim() === '' ? null : Number(amount);

    await notesdb.updateNote(authUser)(
      deviceOrFenceId,
      noteId,
      deviceOrFence,
      {
        note,
        file: finalFilesList,
        po,
        techName,
        completedDate,
        amount: isNaN(amountNumber as number) ? null : amountNumber,
        complete,
        date: moment(creationDate, 'MM-DD-YYYY'),
        updatedAt: moment(),
        extraValues: updatedExtraValues || {},
      },
    );

    back(deviceOrFence === 'fence');
  };

  const dialogRef = useRef<DialogConfigSetter>();
  const extraValuesRef = useRef<iNote['extraValues'] | undefined>(undefined);
  const dynamicSelectOptionsRef = useRef<Record<string, string[]>>({});
  const setupDialog = (callBack: () => DialogConfigSetter) =>
    (dialogRef.current = callBack());

  useEffect(() => {
    extraValuesRef.current = extraValues;
  }, [extraValues]);

  useEffect(() => {
    dynamicSelectOptionsRef.current = dynamicSelectOptions;
  }, [dynamicSelectOptions]);

  const handleCopyNote = () => {
    const getDefaultFieldText = (fieldId: string): string => {
      switch (fieldId) {
        case 'date':
          return creationDate ? `${FIELD_LABELS['date']}: ${creationDate}\n` : '';
        case 'note':
          return note ? `${FIELD_LABELS['note']}: ${note}\n` : '';
        case 'po':
          return po ? `${FIELD_LABELS['po']}: ${po}\n` : '';
        case 'techName':
          return techName ? `${FIELD_LABELS['techName']}: ${techName}\n` : '';
        case 'completedDate':
          return completedDate ? `${FIELD_LABELS['completedDate']}: ${completedDate}\n` : '';
        case 'amount':
          return amount ? `${FIELD_LABELS['amount']}: ${amount}\n` : '';
        case 'complete':
          return complete ? `${FIELD_LABELS['complete']}: Yes\n` : '';
        case 'file':
          const totalFiles = (existingFiles?.length || 0) + (filesToUpload?.length || 0);
          return totalFiles > 0 ? `${FIELD_LABELS['file']}: ${totalFiles}\n` : '';
        default:
          return '';
      }
    };

    let textToCopy = '';
    if (vehicleName) {
      textToCopy += `Vehicle: ${vehicleName}\n`;
    }

    const DEFAULT_ORDER = ['date', 'po', 'techName', 'completedDate', 'amount', 'complete', 'file', 'note'];

    if (!fieldOrder) return;

    fieldOrder.forEach((fieldId) => {
      if (DEFAULT_ORDER.includes(fieldId)) {
        textToCopy += getDefaultFieldText(fieldId);
      } else {
        // Handle dynamic fields
        const field = extraValues?.[fieldId];
        if (field && field.value !== undefined && field.value !== null && field.value !== '') {
          const fieldDef = noteFieldDefs[fieldId]?.details;
          const label = fieldDef?.name || fieldId;
          const value = formatFieldValue(field.value);
          textToCopy += `${label}: ${value}\n`;
        }
      }
    });

    navigator.clipboard
      .writeText(textToCopy)
  };

  const remove = async () => {
    const dialog = dialogRef.current;

    dialog?.().then((confirmed) => {
      if (!confirmed) return;

      if (authUser) {
        notesdb.deleteNote(authUser)(deviceOrFenceId, noteId, deviceOrFence);
      }

      back(deviceOrFence === 'fence');
    });
  };

  const attachFieldToNote = (fieldId: string) => {
    if (!fieldId) return;

    const def = noteFieldDefs[fieldId]?.details;
    const effectiveKind: NoteFieldKind = (def?.kind as NoteFieldKind | undefined) || 'text';

    let initialValue: string | boolean = '';
    if (effectiveKind === 'boolean') initialValue = false;

    setExtraValues((prev) => {
      const current = prev || {};
      if (current[fieldId]) return current;
      const next = {
        ...current,
        [fieldId]: {
          fieldId,
          value: initialValue,
        },
      };
      return next;
    });

    setFieldOrder((order) => {
      if (!order) return [fieldId];
      return order.includes(fieldId) ? order : [...order, fieldId];
    });
  };

  const handleExtraValueChange = (
    fieldId: string,
    value: string | boolean,
  ) => {
    setExtraValues((prev) => {
      const current = prev || {};
      return {
        ...current,
        [fieldId]: { ...(current[fieldId] || {}), fieldId, value },
      };
    });
  };

  // Date picker modal handlers
  const openDatePicker = (fieldId: string, currentValue: string) => {
    const parsedDate = currentValue ? moment(currentValue, 'MM-DD-YYYY').toDate() : new Date();
    setCurrentDateFieldId(fieldId);
    setSelectedDate(parsedDate);
    setDisplayMonth(parsedDate);
    setShowDatePicker(true);
  };

  const changeInputDate = (value: string) => {
    if (!value) {
      setSelectedDate(new Date());
      setDisplayMonth(new Date());
      return;
    }
    const parsed = moment(value, CALENDAR_INPUT_FORMAT, true);
    if (parsed.isValid()) {
      const date = parsed.toDate();
      setSelectedDate(date);
      setDisplayMonth(date);
    }
  };

  const handleDateSelect = (day: Date) => {
    const date = moment(day).format('MM-DD-YYYY');
    const fieldId = currentDateFieldId;

    if (!fieldId) return;

    // Update the appropriate field
    if (fieldId === 'date') {
      setCreationDate(date);
    } else if (fieldId === 'completedDate') {
      setCompletedDate(date);
    } else {
      // Dynamic field
      handleExtraValueChange(fieldId, date);
    }

    setShowDatePicker(false);
  };

  const addToSelectOptions = (fieldId: string, value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setDynamicSelectOptions((prev) => {
      const list = prev[fieldId] ?? [];
      if (list.includes(trimmed)) return prev;
      return { ...prev, [fieldId]: [...list, trimmed] };
    });
  };

  const handleUpdateField = async (values: { name: string; kind: NoteFieldKind }) => {
    if (!authUser || !editingFieldId) return;

    const normalizedKind = normalizeNoteFieldKind(values?.kind);
    const normalizedName = String(values?.name || '').trim();

    const currentDef = noteFieldDefs[editingFieldId]?.details;
    const isTypeChanged = currentDef && currentDef.kind !== normalizedKind;

    await updateNoteField(authUser)(editingFieldId, {
      name: normalizedName,
      kind: normalizedKind,
    });

    setNoteFieldDefs((prev) => ({
      ...prev,
      [editingFieldId]: {
        details: {
          name: normalizedName,
          kind: normalizedKind,
        },
      },
    }));

    if (isTypeChanged) {
      let newValue: string | boolean = '';

      if (normalizedKind === 'boolean') {
        newValue = false;
      }

      setExtraValues((prev) => ({
        ...prev,
        [editingFieldId]: {
          fieldId: editingFieldId,
          value: newValue,
        }
      }));

      if (normalizedKind !== 'select') {
        setDynamicSelectInputValue(prev => {
          const next = { ...prev };
          delete next[editingFieldId];
          return next;
        });
      }
    }

    setEditingFieldId(null);
  };

  const handleDeleteField = async (fieldId: string) => {
    const dialog = dialogRef.current;

    dialog?.({
      type: 'CONFIRM',
      title: 'Delete Field',
      body: 'Are you sure you want to completely delete this field? It will be removed from all notes.',
    }).then(async (confirmed: boolean) => {
      if (!confirmed) return;

      if (authUser) {
        try {
          await deleteNoteField(authUser)(fieldId);
        } catch (e) {
          console.error("Error deleting field", e);
          return;
        }
      }

      setNoteFieldDefs((prev) => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });

      setExtraValues((prev) => {
        const next = { ...(prev || {}) };
        delete next[fieldId];
        return next;
      });

      setFieldOrder((prev) => (prev ? prev.filter((id) => id !== fieldId) : null));

      setGlobalVisibleFields((prev) => prev.filter((id) => id !== fieldId));

      setEditingFieldId(null);
    });
  };

  const handleCreateField = async (values: { name: string; kind: NoteFieldKind }) => {
    if (!authUser) return;

    const normalizedKind = normalizeNoteFieldKind(values?.kind);
    const normalizedName = String(values?.name || '').trim();

    const id = keyGen();

    if (!id) return;

    await updateNoteField(authUser)(id, {
      name: normalizedName,
      kind: normalizedKind,
    });

    setNoteFieldDefs((prev) => ({
      ...prev,
      [id]: {
        details: {
          name: normalizedName,
          kind: normalizedKind,
        },
      },
    }));

    attachFieldToNote(id);
  };

  const handleHideOption = async () => {
    if (!authUser || !optionToDelete) return;

    const { fieldId, value } = optionToDelete;

    const prefKey = getPrefKey(value);
    setHiddenOptions(prev => ({
      ...prev,
      [fieldId]: { ...(prev[fieldId] || {}), [prefKey]: true }
    }));

    await notesdb.hideSuggestionOption(authUser)(fieldId, value);
    // toast.success(`Option "${value}" removed from suggestions.`);
    setOptionToDelete(null);
  };

  const handleSaveOptionEdit = async (newValue: string) => {
    if (!optionToEdit || !authUser) return;
    const { fieldId, value: oldValue } = optionToEdit;

    const trimmedNewValue = String(newValue || '').trim();
    if (!trimmedNewValue) return;

    const oldPrefKey = getPrefKey(oldValue);

    setRenameOptions(prev => ({
      ...prev,
      [fieldId]: { ...(prev[fieldId] || {}), [oldPrefKey]: trimmedNewValue }
    }));

    await notesdb.renameSuggestionOption(authUser)(fieldId, oldValue, trimmedNewValue);

    if (fieldId === 'techName') {
      setTechNameSuggestions(prev => {
        const next = prev.map(v => v === oldValue ? trimmedNewValue : v);
        return Array.from(new Set(next));
      });

      if (techName === oldValue || resolveSuggestionLabel('techName', techName) === oldValue) {
        setTechName(trimmedNewValue);
        setInputValue('');
      }
    } else {
      setDynamicSelectOptions(prev => {
        const currentOptions = prev[fieldId] || [];
        const nextOptions = currentOptions.map(v => v === oldValue ? trimmedNewValue : v);
        return { ...prev, [fieldId]: Array.from(new Set(nextOptions)) };
      });

      setExtraValues(prev => {
        const current = prev || {};
        const existing = current[fieldId];

        if (!existing) return prev;

        const currentValue = existing.value != null ? String(existing.value) : '';
        const resolvedCurrentValue = currentValue ? resolveSuggestionLabel(fieldId, currentValue) : '';

        if (currentValue !== oldValue && resolvedCurrentValue !== oldValue) {
          return prev;
        }

        return {
          ...current,
          [fieldId]: {
            ...existing,
            fieldId,
            value: trimmedNewValue,
          },
        };
      });

      setDynamicSelectInputValue(prev => {
        const currentTyped = prev[fieldId];
        if (!currentTyped || (currentTyped !== oldValue && currentTyped.trim() !== oldValue)) {
          return prev;
        }

        return {
          ...prev,
          [fieldId]: trimmedNewValue,
        };
      });
    }

    // toast.success(`Suggestion updated to "${trimmedNewValue}".`);
    setOptionToEdit(null);
  };

  const onEditClick = (fId: string, val: string) => setOptionToEdit({ fieldId: fId, value: val });
  const onHideClick = (fId: string, val: string) => setOptionToDelete({ fieldId: fId, value: val });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setFieldOrder((items) => {
      if (!items) return items;

      const oldIndex = items.indexOf(active.id.toString());
      const newIndex = items.indexOf(over.id.toString());

      if (oldIndex === -1 || newIndex === -1) return items;

      const newOrder = arrayMove(items, oldIndex, newIndex);

      if (authUser) {
        notesdb.saveFieldOrder(authUser)(newOrder).catch(console.error);
      }

      return newOrder;
    });
  };



  const handleMainDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setMainDropZoneHovered(true);
  };

  const handleMainDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setMainDropZoneHovered(false);
  };

  const handleMainDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setMainDropZoneHovered(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles = Array.from(e.dataTransfer.files);
      setFilesToUpload(prev => [...prev, ...newFiles]);
    }
  };

  const handleDynamicDragOver = (e: DragEvent<HTMLDivElement>, fieldId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDynamicDropZonesHovered(prev => ({ ...prev, [fieldId]: true }));
  };

  const handleDynamicDragLeave = (e: DragEvent<HTMLDivElement>, fieldId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDynamicDropZonesHovered(prev => ({ ...prev, [fieldId]: false }));
  };

  const handleDynamicDrop = (e: DragEvent<HTMLDivElement>, fieldId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDynamicDropZonesHovered(prev => ({ ...prev, [fieldId]: false }));

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const selectedFile = e.dataTransfer.files[0];
      setDynamicFieldFiles((prev) => ({
        ...prev,
        [fieldId]: selectedFile,
      }));
    }
  };

  const renderField = (fieldId: string, dragProps: SortableDragProps) => {
    const labelStyle = { cursor: 'grab', touchAction: 'none' as const };
    const dragLabelProps = {
      ...dragProps.attributes,
      ...dragProps.listeners,
      style: labelStyle,
    };

    const isDefaultField = DEFAULT_ORDER.includes(fieldId);
    switch (fieldId) {
      case 'note':
        return (
          <div className={styles.gridRow}>
            <div className={styles.gridCell} {...dragLabelProps}>
              {isDefaultField && <span style={{ fontSize: 10, color: '#999', marginLeft: "1px", marginRight: "10.35px" }}>✓</span>}Note:
            </div>
            <div className={`${styles.gridCellLast} ${styles.gridCellTextarea}`}>
              <textarea
                autoFocus
                name="note-textarea"
                className={`${styles.textareaGrid} mas-input`}
                placeholder="Type new note here..."
                value={note}
                onChange={({ target }) => updateNote(target.value)}
              />
            </div>
          </div>
        );

      case 'po':
        return (
          <div className={styles.gridRow}>
            <div className={styles.gridCell} {...dragLabelProps}>
              Purchase Order:
            </div>
            <div className={styles.gridCellLast}>
              <input
                type="text"
                className={`${styles.inputGrid} mas-input`}
                placeholder="PO Number"
                value={po}
                onChange={({ target }) => setPo(target.value)}
              />
            </div>
          </div>
        );

      case 'techName':
        const visibleTechSuggestions = techNameSuggestions
          .map((rawValue) => String(rawValue || '').trim())
          .filter((rawValue) => !!rawValue && !isSuggestionHidden('techName', rawValue))
          .map((rawValue) => resolveSuggestionLabel('techName', rawValue));

        const techOptionsSet = new Set<string>(visibleTechSuggestions);

        const currentTechLabel = techName ? resolveSuggestionLabel('techName', techName) : '';
        const techOptionsList = Array.from(techOptionsSet).map(name => ({ value: name, label: name }));

        if (techName && !techOptionsSet.has(techName)) {
          techOptionsList.unshift({ value: techName, label: currentTechLabel || techName });
        }

        return (
          <div className={styles.gridRow}>
            <div className={styles.gridCell} style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <span {...dragLabelProps}>Tech Name:</span>
            </div>
            <div className={styles.gridCellLast}>
              <CreatableSelect
                isClearable
                components={{ SingleValue: CopyableSingleValue, Option: CustomSelectOption }}
                {...({
                  fieldId: 'techName',
                  onEditClick,
                  onHideClick
                } as any)}
                options={techOptionsList}
                createOptionPosition="first"
                value={techName ? { value: techName, label: currentTechLabel || techName } : null}
                inputValue={inputValue}
                onInputChange={(newValue) => setInputValue(newValue)}
                onChange={(selected) => {
                  setTechName(selected?.value || '');
                  setInputValue('');
                }}
                onCreateOption={(newValue) => {
                  setTechName(newValue);
                  setInputValue('');
                  setTechNameSuggestions(prev => {
                    if (!prev.includes(newValue)) {
                      return [newValue, ...prev];
                    }
                    return prev;
                  });
                }}
                onBlur={() => {
                  if (inputValue.trim() && !techNameSuggestions.includes(inputValue.trim())) {
                    setTechName(inputValue.trim());
                    setTechNameSuggestions(prev => [inputValue.trim(), ...prev]);
                  }
                  setInputValue('');
                }}
                onKeyDown={(e) => {
                  if (
                    e.key === 'Enter' &&
                    inputValue.trim() &&
                    !techNameSuggestions.includes(inputValue.trim())
                  ) {
                    e.preventDefault();
                    setTechName(inputValue.trim());
                    setInputValue('');
                    setTechNameSuggestions(prev => [inputValue.trim(), ...prev]);
                  }
                }}
                placeholder="Tech Name"
                className={styles.techNameSelect}
                classNamePrefix="tech-name-select"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                menuPosition="fixed"
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary: 'rgb(255, 124, 2)',
                    primary25: 'rgba(255, 124, 2, 0.12)',
                    primary50: 'rgba(255, 124, 2, 0.22)',
                    primary75: 'rgba(255, 124, 2, 0.35)',
                  },
                })}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
                  control: (base) => ({ ...base, fontSize: 10, minHeight: 24 }),
                  valueContainer: (base) => ({ ...base, paddingTop: 0, paddingBottom: 0 }),
                  input: (base) => ({ ...base, fontSize: 10 }),
                  singleValue: (base) => ({ ...base, fontSize: 10 }),
                  placeholder: (base) => ({ ...base, fontSize: 10 }),
                  menu: (base) => ({ ...base, fontSize: 10 }),
                  option: (base) => ({ ...base, fontSize: 9, paddingTop: 0, paddingBottom: 0 }),
                }}
                filterOption={filterOptionLeftToRight}

              />
            </div>
          </div>
        );

      case 'completedDate':
        return (
          <div className={styles.gridRow}>
            <div className={styles.gridCell}>
              <span {...dragLabelProps}>Completed Date:</span>
            </div>
            <div className={styles.gridCellLast}>
              <input
                type="text"
                value={completedDate}
                readOnly
                onClick={() => openDatePicker('completedDate', completedDate)}
                placeholder="MM-DD-YYYY"
                className={`${styles.calendarInputGrid} mas-input`}
                style={{ cursor: 'pointer', textAlign: 'center' }}
              />
            </div>
          </div>
        );

      case 'amount':
        const parseCurrency = (val: string) => {
          // Remove all non-numeric except . and -
          return val.replace(/[^\d.-]/g, '');
        };
        return (
          <div className={styles.gridRow}>
            <div className={styles.gridCell}>
              <span {...dragLabelProps}>Amount:</span>
            </div>
            <div className={styles.gridCellLast}>
              <input
                type="text"
                inputMode="decimal"
                className={`${styles.inputGrid} mas-input`}
                placeholder="Amount"
                value={amount}
                onChange={({ target }) => {
                  // Allow user to type, but keep only valid numeric chars
                  const raw = parseCurrency(target.value);
                  setAmount(raw);
                }}
                onBlur={({ target }) => {
                  // On blur, format as currency
                  const raw = parseCurrency(target.value);
                  setAmount(raw);
                }}
              />
            </div>
          </div>
        );

      case 'complete':
        return (
          <div className={styles.gridRow}>
            <div className={styles.gridCell}>
              <span {...dragLabelProps}>Complete:</span>
            </div>
            <div className={styles.gridCellLast}>
              <label className={styles.toggleRow}>
                <Checkbox checked={complete} onClick={() => setComplete(!complete)} />
                <span className={styles.completeLabel}>{complete ? 'Yes' : 'Yes'}</span>
              </label>
            </div>
          </div>
        );

      case 'file':
        return (
          <div className={styles.gridRow}>
            <div className={styles.gridCell}>
              <span {...dragLabelProps}>Files:</span>
            </div>
            <div
              className={`${styles.gridCellLast} ${styles.gridCellFile} ${mainDropZoneHovered ? styles.dragOver : ''}`}
              onDragOver={handleMainDragOver}
              onDragLeave={handleMainDragLeave}
              onDrop={handleMainDrop}
              style={{
                border: mainDropZoneHovered ? '2px dashed #FF7C02' : '1px solid transparent',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
                position: 'relative',
              }}
            >

              <div className={styles.filesList}>
                {existingFiles.map((f, i) => (
                  <div key={`existing-${i}`} className={styles.filePreviewGrid}>
                    <span
                      className={styles.fileNameGrid}
                      style={{ color: "blue", cursor: "pointer", textDecoration: "underline" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const format = f?.format || (f?.url?.toLowerCase().includes('.pdf') ? 'pdf' : 'image');
                        const galleryFile: GalleryFile = {
                          fileName: f?.fileName || 'Attachment',
                          url: f?.url || '',
                          format: format as 'pdf' | 'image'
                        };
                        openGallery([galleryFile], 0);
                      }}
                    >
                      {f?.fileName}
                    </span>
                    <button type="button" onClick={() => setExistingFiles(prev => prev.filter((_, idx) => idx !== i))} className={styles.removeFileBtnGrid}>Remove</button>
                  </div>
                ))}
                {filesToUpload.map((f, i) => (
                  <div key={`new-${i}`} className={styles.filePreviewGrid}>
                    <span className={styles.fileNameGrid} style={{ fontStyle: 'italic' }}>{f.name}</span>
                    <button type="button" onClick={() => setFilesToUpload(prev => prev.filter((_, idx) => idx !== i))} className={styles.removeFileBtnGrid}>Remove</button>
                  </div>
                ))}
              </div>

              <div className={styles.fileInputWrapperGrid} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={(e) => {
                    if (e.target.files) {
                      const newFiles = Array.from(e.target.files);
                      setFilesToUpload(prev => [...prev, ...newFiles]);
                    }
                  }}
                  className={styles.fileInput}
                  id="file-upload"
                  style={{ display: 'none' }}
                />
                <label htmlFor="file-upload" className={styles.fileInputLabelGrid}>
                  <span className={styles.fileInputTextGrid}>Choose files...</span>
                  <span className={styles.fileInputButtonGrid}>Browse</span>
                </label>
              </div>
              {error && <span className={styles.errorTextGrid}>{error}</span>}
              {uploading && <span className={styles.uploadingTextGrid}>Uploading...</span>}
            </div>
          </div>
        );

      case 'date':
          return (
            <div className={styles.gridRow}>
              <div className={styles.gridCell} {...dragLabelProps}>
                {isDefaultField && <span style={{ fontSize: 9, color: '#999', marginRight: "11px" }}>✓</span>}
                Creation Date:
              </div>
              <div className={styles.gridCellLast}>
                <input
                  type="text"
                  value={creationDate}
                  readOnly
                  onClick={() => openDatePicker('date', creationDate)}
                  placeholder="MM-DD-YYYY"
                  className={`${styles.calendarInputGrid} mas-input`}
                  style={{ cursor: 'pointer', textAlign: 'center' }}
                />
              </div>
            </div>
          );

      default:
        return null;
    }
  };

  const renderDynamicField = (
    fieldId: string,
    dragProps: SortableDragProps,
  ): React.ReactNode => {
    const def = noteFieldDefs[fieldId]?.details;
    const data = extraValues?.[fieldId];
    if (!def || !data) return null;

    const kind: NoteFieldKind = def.kind || 'text';

    let selectOptionsList: { value: string; label: string }[] = [];
    if (kind === 'select') {
      const baseOpts = dynamicSelectOptions[fieldId] ?? [];

      const visibleRenamed = baseOpts
        .map((rawValue) => String(rawValue || '').trim())
        .filter((rawValue) => !!rawValue && !isSuggestionHidden(fieldId, rawValue))
        .map((rawValue) => resolveSuggestionLabel(fieldId, rawValue));

      const optionsSet = new Set<string>(visibleRenamed);

      selectOptionsList = Array.from(optionsSet).map((s) => ({ value: s, label: s }));

      const currentVal = data.value != null ? String(data.value) : '';
      const currentLabel = currentVal ? resolveSuggestionLabel(fieldId, currentVal) : '';

      if (currentVal && !optionsSet.has(currentVal)) {
        selectOptionsList.unshift({ value: currentVal, label: currentLabel || currentVal });
      }
    }

    const labelStyle = { cursor: 'grab', touchAction: 'none' as const };
    const dragLabelProps = {
      ...dragProps.attributes,
      ...dragProps.listeners,
      style: labelStyle,
    };

    return (
      <div className={styles.gridRow}>
        <div className={styles.gridCell} style={{ display: 'flex', alignItems: 'center', gap: '3px', overflow: "hidden" }}>
          <span {...dragLabelProps}>{def.name}:</span>
          <button
            type="button"
            className={styles.editFieldBtn}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setEditingFieldId(fieldId);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            title="Edit label"
          >
            <Fa icon={faPencilAlt}/>
          </button>
        </div>
        <div className={styles.gridCellLast}>
          {kind === 'boolean' ? (
            <label className={styles.toggleRow}>
              <Checkbox
                checked={!!data.value}
                onClick={() => handleExtraValueChange(fieldId, !data.value)}
              />
              <span className={styles.completeLabel}>
                {data.value ? 'Yes' : 'Yes'}
              </span>
            </label>
          ) : kind === 'number' ? (
            <input
              type="text"
              inputMode="decimal"
              className={`${styles.inputGrid} mas-input`}
              value={data.value === undefined || data.value === null ? '' : String(data.value)}
              onChange={e => {
                // Remove commas and non-numeric except . and -
                const raw = e.target.value.replace(/[^\d.-]/g, '');
                handleExtraValueChange(fieldId, raw);
              }}
              onBlur={e => {
                // Optionally, reformat on blur
                const raw = e.target.value.replace(/[^\d.-]/g, '');
                handleExtraValueChange(fieldId, raw);
              }}
            />
          ) : kind === 'textarea' ? (
            <textarea
              className={`${styles.textareaGrid} mas-input`}
              value={String(data.value ?? '')}
              onChange={(e) =>
                handleExtraValueChange(fieldId, e.target.value)
              }
            />
          ) : kind === 'date' ? (
            <input
              type="text"
              value={String(data.value ?? '')}
              readOnly
              onClick={() => openDatePicker(fieldId, String(data.value ?? ''))}
              placeholder="MM-DD-YYYY"
              className={`${styles.calendarInputGrid} mas-input`}
              style={{ cursor: 'pointer', textAlign: 'center' }}
            />
          ) : kind === 'select' ? (
            <CreatableSelect
              isClearable
              components={{ SingleValue: CopyableSingleValue, Option: CustomSelectOption }}
              {...({
                fieldId,
                onEditClick,
                onHideClick
              } as any)}
              options={selectOptionsList}
              createOptionPosition="first"
              value={
                data.value
                  ? {
                    value: String(data.value),
                    label: resolveSuggestionLabel(fieldId, String(data.value)) || String(data.value),
                  }
                  : null
              }
              inputValue={dynamicSelectInputValue[fieldId] ?? ''}
              onInputChange={(newValue) =>
                setDynamicSelectInputValue((prev) => ({
                  ...prev,
                  [fieldId]: newValue,
                }))
              }
              onChange={(option) => {
                const val = option?.value ? String(option.value) : '';
                addToSelectOptions(fieldId, val);
                handleExtraValueChange(fieldId, val);
                setDynamicSelectInputValue((prev) => {
                  const next = { ...prev };
                  delete next[fieldId];
                  return next;
                });
              }}
              onCreateOption={(newValue) => {
                addToSelectOptions(fieldId, newValue);
                handleExtraValueChange(fieldId, newValue);
                setDynamicSelectInputValue((prev) => {
                  const next = { ...prev };
                  delete next[fieldId];
                  return next;
                });
              }}
              onBlur={() => {
                const typed = (dynamicSelectInputValue[fieldId] ?? '').trim();
                if (typed && String(data.value ?? '') !== typed) {
                  addToSelectOptions(fieldId, typed);
                  handleExtraValueChange(fieldId, typed);
                }
                setDynamicSelectInputValue((prev) => {
                  const next = { ...prev };
                  delete next[fieldId];
                  return next;
                });
              }}
              onKeyDown={(e) => {
                const typed = (dynamicSelectInputValue[fieldId] ?? '').trim();
                if (
                  e.key === 'Enter' &&
                  typed &&
                  String(data.value ?? '') !== typed
                ) {
                  e.preventDefault();
                  addToSelectOptions(fieldId, typed);
                  handleExtraValueChange(fieldId, typed);
                  setDynamicSelectInputValue((prev) => {
                    const next = { ...prev };
                    delete next[fieldId];
                    return next;
                  });
                }
              }}
              placeholder="Value"
              className={styles.techNameSelect}
              classNamePrefix="tech-name-select"
              menuPortalTarget={
                typeof document !== 'undefined' ? document.body : undefined
              }
              menuPosition="fixed"
              theme={(theme) => ({
                ...theme,
                colors: {
                  ...theme.colors,
                  primary: 'rgb(255, 124, 2)',
                  primary25: 'rgba(255, 124, 2, 0.12)',
                  primary50: 'rgba(255, 124, 2, 0.22)',
                  primary75: 'rgba(255, 124, 2, 0.35)',
                },
              })}
              styles={{
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 999999,
                }),
                control: (base) => ({
                  ...base,
                  fontSize: 10,
                  minHeight: 24,
                }),
                valueContainer: (base) => ({
                  ...base,
                  paddingTop: 0,
                  paddingBottom: 0,
                }),
                input: (base) => ({
                  ...base,
                  fontSize: 10,
                }),
                singleValue: (base) => ({
                  ...base,
                  fontSize: 10,
                }),
                placeholder: (base) => ({
                  ...base,
                  fontSize: 10,
                }),
                menu: (base) => ({
                  ...base,
                  fontSize: 10,
                }),
                option: (base) => ({
                  ...base,
                  fontSize: 9,
                  paddingTop: 0,
                  paddingBottom: 0,
                }),
              }}
              filterOption={filterOptionLeftToRight}
            />
          ) : kind === 'file' ? (
            <div
              onDragOver={(e) => handleDynamicDragOver(e, fieldId)}
              onDragLeave={(e) => handleDynamicDragLeave(e, fieldId)}
              onDrop={(e) => handleDynamicDrop(e, fieldId)}
              style={{
                border: dynamicDropZonesHovered[fieldId] ? '2px dashed #FF7C02' : '1px solid transparent',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
                width: '100%'
              }}
            >
              {(() => {
                const value = data.value as ExtraFieldValue;

                const parsedFromString =
                  typeof value === 'string' && value.startsWith('{')
                    ? (() => {
                      try {
                        return JSON.parse(value);
                      } catch {
                        return null;
                      }
                    })()
                    : null;

                const fileInfo: iNote['file'] | null =
                  value && typeof value === 'object' && 'url' in value
                    ? (value as iNote['file'])
                    : parsedFromString && parsedFromString.url
                      ? (parsedFromString as iNote['file'])
                      : null;

                const isUploading = dynamicFieldUploading[fieldId];
                const hasFile = fileInfo?.url || dynamicFieldFiles[fieldId] || value;

                return hasFile ? (
                  <div className={styles.filePreviewGrid}>
                    <span
                      className={styles.fileNameGrid}
                      style={{ color: !fileInfo ? "#37474F" : "blue", cursor: !fileInfo ? "cursor" : "pointer", textDecoration: "underline", fontStyle: !fileInfo ? "italic" : "normal" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!fileInfo) return;
                        const format = fileInfo?.format || (fileInfo?.url?.toLowerCase().includes('.pdf') ? 'pdf' : 'image');
                        const galleryFile: GalleryFile = {
                          fileName: fileInfo?.fileName || 'Attachment',
                          url: fileInfo?.url || '',
                          format: format as 'pdf' | 'image'
                        };
                        openGallery([galleryFile], 0);
                      }}
                    >
                      {fileInfo?.fileName ||
                        dynamicFieldFiles[fieldId]?.name ||
                        (typeof value === 'string' ? value : '')}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        handleExtraValueChange(fieldId, '');
                        setDynamicFieldFiles((prev) => {
                          const next = { ...prev };
                          delete next[fieldId];
                          return next;
                        });
                      }}
                      className={styles.removeFileBtnGrid}
                      disabled={isUploading}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className={styles.fileInputWrapperGrid} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      className={styles.fileInput}
                      id={`extra-file-${fieldId}`}
                      onChange={(e) => {
                        const selectedFile = e.target.files?.[0];
                        if (selectedFile) {
                          setDynamicFieldFiles((prev) => ({
                            ...prev,
                            [fieldId]: selectedFile,
                          }));
                        }
                      }}
                      disabled={isUploading}
                      style={{ display: 'none' }}
                    />
                    <label
                      htmlFor={`extra-file-${fieldId}`}
                      className={styles.fileInputLabelGrid}
                    >
                      <span className={styles.fileInputTextGrid}>
                        Choose file...
                      </span>
                      <span className={styles.fileInputButtonGrid}>Browse</span>
                    </label>
                  </div>
                );
              })()}
              {dynamicFieldUploading[fieldId] && (
                <span className={styles.uploadingTextGrid}>Uploading...</span>
              )}
            </div>
          ) : (
            <input
              type="text"
              className={`${styles.inputGrid} mas-input`}
              value={String(data.value ?? '')}
              onChange={(e) =>
                handleExtraValueChange(fieldId, e.target.value)
              }
            />
          )}
        </div>
      </div>
    );
  };

  const title = action === 'add' ? 'Add New Note' : 'Edit Note';
  const showVehicleNameBadge = deviceOrFence === 'device' && !!vehicleName;
  const DynamicLayout: any = deviceOrFence === 'fence' ? FenceBodyLayout : DashboardBlock;

  const DynamicHeader =
    deviceOrFence === 'fence' ? (
      <>
        <ActionRow title={ownerTitle ?? 'no title'} canBack actions={[]} />
        <h4 style={{ marginLeft: 8 }}>{title}</h4>
      </>
    ) : (
      <ModalHeader
        title={(
          <span className={styles.smallHeader}>
            <span>{title}</span>
            {showVehicleNameBadge && (
              <span className={styles.vehicleNameBadge}>{vehicleName}</span>
            )}
          </span>
        )}
        className={styles.modalHeader}
      />
    );

  if (isLoading || !fieldOrder) {
    return (
      <DynamicLayout mapOnly={!deviceOrFenceId || !deviceOrFence}>
        {DynamicHeader}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
          <Loader />
        </div>
      </DynamicLayout>
    );
  }

  const additionalFieldsCount = globalVisibleFields.filter(f => !DEFAULT_VISIBLE_FIELDS.includes(f)).length;

  return (
    <DynamicLayout mapOnly={!deviceOrFenceId || !deviceOrFence}>
      {DynamicHeader}

      <div className={styles.gridContainer}>
        <div className={styles.gridTopBar}>
          <button
            type="button"
            className={styles.addLabelBtn}
            onClick={() => setIsCreateFieldOpen(true)}
          >
            + Create new field
          </button>
        </div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={fieldOrder} strategy={verticalListSortingStrategy}>
            {fieldOrder.map((fieldId) => (
              <SortableRow key={fieldId} id={fieldId}>
                {(dragProps) =>
                  DEFAULT_ORDER.includes(fieldId)
                    ? renderField(fieldId, dragProps)
                    : renderDynamicField(fieldId, dragProps)
                }
              </SortableRow>
            ))}
          </SortableContext>
        </DndContext>
      </div>
      <div style={{marginTop: 'auto'}}>
        <ModalActions
          isFence={deviceOrFence === 'fence'}
          buttons={
            action === 'add'
              ? [
              { click: () => back(deviceOrFence === 'fence'), title: 'Cancel' },
              { click: handleCopyNote, title: 'Copy to\nClipboard', multiline: true, customClassName: styles.copyButtonBlue },
              { click: update, title: 'Create', primary: true, customClassName: styles.updateButtonOrange },
            ]
            : [
              { click: remove, title: 'Delete', danger: true},
              { click: handleCopyNote, title: 'Copy note', customClassName: styles.copyButtonBlue },
              { click: () => back(deviceOrFence === 'fence'), title: 'Cancel',  primary: true },
              { click: update, title: 'Update', primary: true, customClassName: styles.updateButtonOrange },
            ]
          }
          embedded
        />
      </div>
      {isCreateFieldOpen && (
        <NewNoteFieldModal
          onClose={() => setIsCreateFieldOpen(false)}
          onSubmit={handleCreateField}
        />
      )}

      {editingFieldId && noteFieldDefs[editingFieldId] && (
        <NewNoteFieldModal
          onClose={() => setEditingFieldId(null)}
          onSubmit={handleUpdateField}
          onDelete={() => handleDeleteField(editingFieldId)}
          initialValues={{
            name: noteFieldDefs[editingFieldId].details.name,
            kind: normalizeNoteFieldKind(noteFieldDefs[editingFieldId].details.kind)
          }}
        />
      )}

      {optionToEdit && (
        <EditOptionModal
          value={optionToEdit.value}
          onClose={() => setOptionToEdit(null)}
          onSave={handleSaveOptionEdit}
        />
      )}

      <ClearModal
        open={!!optionToDelete}
        onClose={() => setOptionToDelete(null)}
        onConfirm={handleHideOption}
        title="Hide Suggestion"
        bodyText={`Are you sure you want to hide "${optionToDelete?.value}" from the list of suggestions?`}
      />

      <Dialog setupConfig={setupDialog} title="Confirm Note Delete">
        Are you sure you want to delete? This action cannot be undone.
      </Dialog>

      {isGalleryOpen && (
        <GalleryModal
          open={isGalleryOpen}
          files={galleryFiles}
          initialIndex={galleryIndex}
          onClose={() => setIsGalleryOpen(false)}
        />
      )}

      {showDatePicker && (
        <Modal>
          <div className={styles.dayPickerContainer}>
            {/* Date Input */}
            <MaskedInput
              id="dateInput"
              className={styles.maskedDateInput}
              mask={dateMask}
              guide={false}
              value={
                selectedDate
                  ? moment(selectedDate).format(CALENDAR_INPUT_FORMAT)
                  : ''
              }
              onChange={(e) => changeInputDate(e.target.value.trim())}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setShowDatePicker(false);
                  handleDateSelect(selectedDate);
                }
              }}
            />

            {/* Calendar */}
            <DayPicker
              selectedDays={selectedDate}
              month={displayMonth}
              onMonthChange={(month) => setDisplayMonth(month)}
              onDayClick={handleDateSelect}
              showOutsideDays
              modifiers={{ today: new Date() }}
              modifiersStyles={{
                today: {
                  color: '#FF7C02',
                  fontWeight: 'bold',
                  backgroundColor: '#FFEFE6',
                },
                outside: {
                  color: '#ccc',
                },
              }}
              navbarElement={(navbarProps) => (
                <CustomNavBar
                  {...navbarProps}
                  handleMonthChange={changeInputDate}
                />
              )}
              captionElement={() => null}
            />

            {/* Close icon button */}
            <button
              onClick={() => {
                setShowDatePicker(false);
                handleDateSelect(selectedDate);
              }}
              className={styles.dayPickerClose}
            >
              <CloseIcon style={{ fill: '#878889' }} />
            </button>

            {/* Cancel and OK buttons */}
            <div className={styles.dayPickerFooter}>
              <button
                className={styles.cancelButton}
                onClick={() => {
                  setShowDatePicker(false);
                }}
              >
                Cancel
              </button>

              <button
                className={styles.okButton}
                onClick={() => {
                  setShowDatePicker(false);
                  handleDateSelect(selectedDate);
                }}
              >
                Ok
              </button>
            </div>
          </div>
        </Modal>
      )}
    </DynamicLayout>
  );
};

export default withSentry(AddNote);
